import { format } from 'date-fns';
import { Badge } from "~/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "~/components/ui/table";
import LocalDateTime from '~/lib/localdatetime';

// Props type definition (more specific than any[])
type Appointment = {
    id: string;
    steps?: Array<{
        name?: string;
        dateFromLocal?: LocalDateTime;
        dateUntilLocal?: LocalDateTime;
        practitioners: {
            practitioner: {
                id: number;
                title: string | null;
                firstName: string;
                lastName: string;
            }
        }[];
    }>;
    originalTreatmentVariants?: Array<{ treatmentVariant?: { name?: string | null } | null }>;
    titleShort?: string | null;
    title?: string | null;
    bookedPrice?: number | string | object | null; // Handle Decimal object potentially
    status?: string | null;
    store: {
        name: string;
    } | null
};

type CustomerAppointmentListProps = {
    appointments: Appointment[];
};

export const CustomerAppointmentList = ({ appointments }: CustomerAppointmentListProps) => {
    if (!appointments || appointments.length === 0) {
        return <p className="text-xs text-muted-foreground italic py-2 text-center">Keine vergangenen Termine gefunden.</p>;
    }

    const formatDateRange = (start?: LocalDateTime, end?: LocalDateTime): string => {
        if (!start) return 'Date N/A';

        const startDateStr = start.format('dd MMM yy'); // Shorter format maybe?
        const startTimeStr = start.format('HH:mm');
        const endTimeStr = !!end ? end.format('HH:mm') : '';
        return `${startDateStr}, ${startTimeStr}${endTimeStr ? ` - ${endTimeStr}` : ''}`;
    }

    return (
        <Table className="table-fixed w-full">
            <TableHeader>
                <TableRow>
                    <TableHead className="w-[150px]">Datum & Uhrzeit</TableHead>
                    <TableHead>Behandlung</TableHead> {/* Flexible width column */}
                    <TableHead>Behandler</TableHead>
                    <TableHead className="w-[100px]">Clinic</TableHead>
                    <TableHead className="w-[100px]">Status</TableHead>
                    {/* <TableHead className="text-right w-[80px]">Preis</TableHead> */}
                </TableRow>
            </TableHeader>
            <TableBody>
                {appointments.map((app) => {
                    const sortedSteps = app.steps?.sort((a, b) => (a.dateUntilLocal?.toUTC().getTime() || 0) - (b.dateUntilLocal?.toUTC().getTime() || 0)) || [];

                    const earliestStart = sortedSteps[0].dateFromLocal;
                    const latestEnd = sortedSteps[sortedSteps.length - 1]?.dateUntilLocal;
                    const treatmentNames = app.originalTreatmentVariants
                        ?.map((v) => v?.treatmentVariant?.name)
                        .filter(Boolean)
                        .join(' + ');
                    const displayTitle = treatmentNames || app.title || 'Appointment';
                    const bookedPrice = typeof app.bookedPrice === 'object' && app.bookedPrice !== null ? app.bookedPrice.toString() : Number(app.bookedPrice ?? 0);

                    const practitionersSet = new Set<string>();
                    if (app.steps) {
                        for (const step of app.steps) {
                            const practitioners = step.practitioners.map((assoc) => assoc.practitioner);
                            for (const practitioner of practitioners) {
                                const name = `${!!practitioner.title && practitioner.title.length > 0 ? `${practitioner.title} ` : ''} ${practitioner.firstName} ${practitioner.lastName}`
                                practitionersSet.add(name);
                            }
                        }
                    }

                    const practitionersList = Array.from(practitionersSet);

                    return (
                        <TableRow key={app.id}>
                            <TableCell className="whitespace-pre-wrap py-1.5 align-top text-xs font-medium">
                                {formatDateRange(earliestStart, latestEnd)}
                            </TableCell>
                            <TableCell className="py-1.5 align-top wrap-anywhere whitespace-pre-wrap text-xs">
                                {displayTitle}
                            </TableCell>
                            <TableCell className="py-1.5 align-top wrap-anywhere whitespace-pre-wrap text-xs">
                                {practitionersList.map((name, idx) => (
                                    <Badge
                                        key={`appointmnet-${app.id}-practitioner-badge-${idx}`}
                                        variant='outline'
                                        className="text-xs px-1.5 py-0.5 whitespace-pre-wrap" // Smaller badge
                                    >
                                        {name}
                                    </Badge>
                                ))}
                            </TableCell>
                            <TableCell className="py-1.5 align-top text-xs">
                                <Badge
                                    variant='outline'
                                    className="text-xs px-1.5 py-0.5" // Smaller badge
                                >
                                    {app.store?.name || 'Digital'}
                                </Badge>
                            </TableCell>
                            <TableCell className="py-1.5 align-top text-xs">
                                <Badge
                                    variant={app.status === 'COMPLETED' ? 'default' : ['CANCELLED', 'NO_SHOW', 'DECLINED', 'CALLED_OFF'].includes(app.status || '') ? 'destructive' : 'outline'}
                                    className="text-xs px-1.5 py-0.5" // Smaller badge
                                >
                                    {app.status || 'N/A'}
                                </Badge>
                            </TableCell>
                            {/* <TableCell className="text-right py-1.5 align-top text-xs">
                                €{Number(bookedPrice).toFixed(2)}
                            </TableCell> */}
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
};